import { ChevronDown, ChevronUp, Edit, MoreVertical, Reply, Trash2 } from "lucide-react"
import { useState } from "react"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/features/auth/store/authStore"
import { searchUsers } from "@/features/profile/services/user.service"
import { PinIcon } from "../../../components/icons/index"
import type { Comment } from "../services/comment.api"
import { CommentForm } from "./CommentForm"

const MENTION_REGEX = /@(\w+)/g

function renderContentWithMentions(content: string): React.ReactNode {
  const parts = content.split(MENTION_REGEX)
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return (
        <span key={index} className="text-brand-terracotta font-medium">
          @{part}
        </span>
      )
    }
    return part
  })
}

const getCommentUserAvatar = (user: Comment["user"]): string | undefined => {
  const coverMedia = user.profileMedia?.find(media => media.isCover && media.upload)
  return coverMedia?.upload?.filePath
}

interface CommentItemProps {
  comment: Comment
  canReply: boolean
  canModerate: boolean
  onReply: (commentId: string, content: string) => Promise<void>
  onEdit: (commentId: string, content: string) => Promise<void>
  onDelete: (commentId: string) => Promise<void>
  onTogglePin: (commentId: string) => Promise<void>
}

const formatRelativeDate = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMinutes < 1) return "just now"
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export function CommentItem({
  comment,
  canReply,
  canModerate,
  onReply,
  onEdit,
  onDelete,
  onTogglePin,
}: CommentItemProps) {
  const currentUserId = useAuthStore(state => state.user?.id)
  const [isReplying, setIsReplying] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [repliesVisible, setRepliesVisible] = useState(false)

  const isAuthor = currentUserId === comment.userId
  const canPin = canModerate && comment.depthLevel === 0
  const canReplyToComment = canReply && comment.depthLevel < 2 && !comment.deleted
  const hasReplies = comment.replies && comment.replies.length > 0

  const repliesSection =
    hasReplies && repliesVisible ? (
      <div className="mt-4 space-y-3 border-l border-border pl-7 pt-2.5">
        {comment.replies?.map(reply => (
          <CommentItem
            key={reply.id}
            comment={reply}
            canReply={canReply}
            canModerate={canModerate}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
            onTogglePin={onTogglePin}
          />
        ))}
      </div>
    ) : null

  if (comment.deleted) {
    const deletedRepliesSection = hasReplies ? (
      <div className="mt-4 space-y-3 border-l border-border pl-7 pt-2.5">
        {comment.replies?.map(reply => (
          <CommentItem
            key={reply.id}
            comment={reply}
            canReply={canReply}
            canModerate={canModerate}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
            onTogglePin={onTogglePin}
          />
        ))}
      </div>
    ) : null

    return (
      <div className="rounded-xl bg-card px-4 py-3 text-sm italic text-ash">
        {comment.content}
        {deletedRepliesSection}
      </div>
    )
  }

  return (
    <div className="rounded-xl flex flex-col gap-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="w-full">
          <div className="flex justify-between">
            <div className="flex flex-wrap items-center gap-2.5">
              <Avatar
                src={getCommentUserAvatar(comment.user)}
                fallback={comment.user.name}
                size="md"
                className="w-7.5 h-7.5 border"
              />
              <span className="text-sm leading-5.5 font-semibold text-charcoal">
                {comment.user.name}
              </span>
              <span className="text-sm leading-5.5 text-stone">@{comment.user.username}</span>
              <span className="text-xs leading-5.5 text-ash">
                {formatRelativeDate(comment.createdAt)}
                {comment.isEdited ? " (edited)" : ""}
              </span>
            </div>
            {(canPin || isAuthor || canModerate) && (
              <div className="flex shrink-0 items-center">
                {comment.isPinned && (
                  <PinIcon className="h-4 w-4 text-primary" fill="currentColor" />
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canPin && (
                      <DropdownMenuItem onClick={() => onTogglePin(comment.id)}>
                        <PinIcon className="h-4 w-4" />
                        {comment.isPinned ? "Unpin" : "Pin"}
                      </DropdownMenuItem>
                    )}
                    {isAuthor && (
                      <DropdownMenuItem onClick={() => setIsEditing(current => !current)}>
                        <Edit className="h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    {(isAuthor || canModerate) && (
                      <DropdownMenuItem variant="destructive" onClick={() => onDelete(comment.id)}>
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
          {isEditing ? (
            <div className="mt-3">
              <CommentForm
                initialValue={comment.content}
                placeholder="Edit your comment..."
                onCancel={() => setIsEditing(false)}
                onSubmit={async content => {
                  await onEdit(comment.id, content)
                  setIsEditing(false)
                }}
                onSearchUsers={async query => {
                  const result = await searchUsers({ search: query, limit: 10 })
                  return result.users.map(u => ({
                    id: u.id,
                    name: u.name || "",
                    username: u.username || "",
                    profileMedia: u.profileMedia,
                  }))
                }}
              />
            </div>
          ) : (
            <p className="mt-2.5 text-sm leading-relaxed text-charcoal">
              {renderContentWithMentions(comment.content)}
            </p>
          )}
        </div>
      </div>

      {(canReplyToComment || hasReplies) && (
        <div className="flex items-center gap-3">
          {canReplyToComment && (
            <span
              className="flex items-center gap-1.25 text-xs leading-5.5 cursor-pointer text-[var(--color-brand-terracotta)]"
              onClick={() => setIsReplying(current => !current)}
            >
              <Reply className="h-4 w-4 rotate-180" />
              Reply
            </span>
          )}
          {hasReplies && (
            <span
              className="flex items-center gap-1.25 text-xs leading-5.5 cursor-pointer text-[#C86757] hover:text-[#B65A4B] transition-colors"
              onClick={() => setRepliesVisible(!repliesVisible)}
            >
              {repliesVisible ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {repliesVisible ? "Hide replies" : "Show replies"}
            </span>
          )}
        </div>
      )}

      {isReplying && (
        <div className="mt-3 ml-10">
          <p className="mb-3 text-sm font-semibold text-charcoal">Reply to comment</p>
          <CommentForm
            placeholder="Write a reply..."
            onCancel={() => setIsReplying(false)}
            onSubmit={async content => {
              await onReply(comment.id, content)
              setIsReplying(false)
            }}
            onSearchUsers={async query => {
              const result = await searchUsers({ search: query, limit: 10 })
              return result.users.map(u => ({
                id: u.id,
                name: u.name || "",
                username: u.username || "",
                profileMedia: u.profileMedia,
              }))
            }}
          />
        </div>
      )}

      {repliesSection}
    </div>
  )
}
